import { useQuery } from '@tanstack/react-query';
import { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import BackButton from '@/components/BackButton';
import ContentSpinner from '@/components/ContentSpinner';
import { PageHeading, TeamHeading } from '@/components/Layout/Parts';
import useAlerts from '@/hooks/useAlerts';
import { MLProjectTranslation } from '@/types/resources';
import { api } from '@/utils/api';
import { MLProjectTranslationStatus } from '@/utils/enums';

import MainInfoCard from '../MLProject/components/MainInfoCard';
import AssetsCard from './components/AssetsCard';
import MainInfoTranslationsCard from './components/MainInfoTranslationsCard';
import OriginalVideoCard from './components/OriginalVideoCard';
import PreviewVideoCard from './components/PreviewVideoCard';
import ScriptCard from './components/ScriptCard';
import SubtitlesStylesCard from './components/SubtitlesStylesCard';

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 48px;
`;

const MLTranslation: FC = () => {
  const { translationId } = useParams();
  const navigate = useNavigate();
  const alerts = useAlerts();

  const { data: translation, isLoading } = useQuery(
    [
      'ml_translations',
      {
        id: translationId,
      },
    ],
    () =>
      api.get<never, MLProjectTranslation>(
        `/v1/distribution/ml-translations/${translationId}`,
      ),
    {
      retry: 2,
      refetchInterval: 45_000,
      onError: (err) => {
        if ((err as any)?.response?.status === 404) {
          return navigate('/404', {
            replace: true,
          });
        }
        alerts.error();
      },
    },
  );

  const backLink = `../../ml-languages/${translation?.language}/${translation?.status === MLProjectTranslationStatus.DONE ? 'done' : 'pending'}`;

  if (isLoading || !translation) {
    return <ContentSpinner />;
  }

  return (
    <>
      <TeamHeading />
      <PageHeading>{translation.project!.title}</PageHeading>
      <BackButton link={backLink} />
      <Cards>
        <MainInfoCard project={translation.project!} />
        <OriginalVideoCard
          videoVolume={translation.settings?.videoVolume}
          videoMedia={translation.project!.video}
          translationId={translation.id}
        />
        <MainInfoTranslationsCard
          isDisabled={translation.status === MLProjectTranslationStatus.DONE}
          translationId={translation.id}
          title={translation.title}
          description={translation.description}
        />
        <ScriptCard translation={translation} />
        <SubtitlesStylesCard translation={translation} />
        <AssetsCard translation={translation} />
        <PreviewVideoCard translation={translation} />
      </Cards>
    </>
  );
};

export default MLTranslation;
